import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Mic, Headphones, Globe, Zap, Shield, Users } from 'lucide-react';

export function HomePage() {
  const navigate = useNavigate();

  const handleCreateRoom = () => {
    navigate('/speaker');
  };

  const handleJoinRoom = () => {
    navigate('/listener');
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Globe className="h-8 w-8 text-primary" />
            <h1 className="text-4xl font-bold">Real-Time Speech Translation</h1>
          </div>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Break down language barriers with instant speech translation. 
            Speak in one language, and listeners hear it in theirs.
          </p>
          <div className="flex items-center justify-center gap-2 mt-4">
            <Badge variant="secondary">
              <Zap className="h-3 w-3 mr-1" />
              Real-time
            </Badge>
            <Badge variant="secondary">
              <Shield className="h-3 w-3 mr-1" />
              No storage
            </Badge>
            <Badge variant="secondary">
              <Users className="h-3 w-3 mr-1" />
              Multi-listener
            </Badge>
          </div>
        </div>

        {/* Main Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-12">
          <Card className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Mic className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <CardTitle>Start Speaking</CardTitle>
                  <CardDescription>Create a room and share your voice</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <ul className="text-sm text-muted-foreground space-y-2">
                <li>• Create a unique room instantly</li>
                <li>• Share via QR code or link</li>
                <li>• Real-time speech transcription</li>
                <li>• Support multiple listeners</li>
              </ul>
              <Button className="w-full" onClick={handleCreateRoom}>
                <Mic className="h-4 w-4 mr-2" />
                Create Speaker Room
              </Button>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-secondary/10 rounded-lg">
                  <Headphones className="h-6 w-6 text-secondary-foreground" />
                </div>
                <div>
                  <CardTitle>Listen & Translate</CardTitle>
                  <CardDescription>Join a room to receive translations</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <ul className="text-sm text-muted-foreground space-y-2">
                <li>• Join with room ID or QR code</li>
                <li>• Choose your preferred language</li>
                <li>• Live subtitle translations</li>
                <li>• High-quality text rendering</li>
              </ul>
              <Button variant="outline" className="w-full" onClick={handleJoinRoom}>
                <Headphones className="h-4 w-4 mr-2" />
                Join as Listener
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Features */}
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl font-bold text-center mb-8">How It Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center space-y-4">
              <div className="p-4 bg-primary/10 rounded-full w-16 h-16 mx-auto flex items-center justify-center">
                <span className="text-2xl font-bold text-primary">1</span>
              </div>
              <h3 className="font-semibold">Create or Join</h3>
              <p className="text-sm text-muted-foreground">
                Speakers create rooms, listeners join with room ID or QR code
              </p>
            </div>
            <div className="text-center space-y-4">
              <div className="p-4 bg-primary/10 rounded-full w-16 h-16 mx-auto flex items-center justify-center">
                <span className="text-2xl font-bold text-primary">2</span>
              </div>
              <h3 className="font-semibold">Speak Naturally</h3>
              <p className="text-sm text-muted-foreground">
                AI transcribes speech in real-time with high accuracy
              </p>
            </div>
            <div className="text-center space-y-4">
              <div className="p-4 bg-primary/10 rounded-full w-16 h-16 mx-auto flex items-center justify-center">
                <span className="text-2xl font-bold text-primary">3</span>
              </div>
              <h3 className="font-semibold">Instant Translation</h3>
              <p className="text-sm text-muted-foreground">
                Listeners receive live translations in their chosen language
              </p>
            </div>
          </div>
        </div>

        {/* Supported Languages */}
        <div className="max-w-4xl mx-auto mt-12 text-center">
          <h3 className="text-lg font-semibold mb-4">Supported Languages</h3>
          <div className="flex flex-wrap justify-center gap-2">
            {[
              'English', 'Spanish', 'French', 'German', 'Italian', 
              'Portuguese', 'Russian', 'Chinese', 'Japanese', 'Korean'
            ].map((language) => (
              <Badge key={language} variant="outline">
                {language}
              </Badge>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}